import React, { useState, useEffect, useRef } from 'react';

const CONTROL_BASE = 'w-full rounded-lg border bg-white px-3 text-base sm:text-sm text-gray-900 placeholder-gray-400 shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2';
const controlState = (error, disabled) => `${error
  ? 'border-red-400 focus:border-red-500 focus:ring-red-500/30'
  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/30'} ${disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`;

export const FormField = ({ label, error, children, required = false, className = '', hint, htmlFor }) => {
  return (
    <div className={`mb-3 sm:mb-2.5 ${className}`}>
      {label && (
        <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500" aria-hidden="true">*</span>}
          {required && <span className="sr-only">(required)</span>}
        </label>
      )}
      {children}
      {hint && !error && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
};

export const Input = ({
  type = 'text',
  placeholder,
  value,
  onChange,
  error,
  disabled = false,
  className = '',
  ...props
}) => {
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      aria-invalid={error ? true : undefined}
      className={`${CONTROL_BASE} py-2 sm:py-1.5 min-h-[40px] sm:min-h-[34px] ${controlState(error, disabled)} ${className}`}
      {...props}
    />
  );
};

export const Select = ({
  name,
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  error,
  disabled = false,
  className = '',
  menuProps = null,
  ...props
}) => {
  // Always call hooks at the top level
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!menuProps) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuProps]);

  // If no menuProps provided, render native select (keeps existing behavior)
  if (!menuProps) {
    return (
      <select
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        className={`${CONTROL_BASE} py-1.5 pr-8 min-h-[40px] sm:min-h-[34px] ${controlState(error, disabled)} ${className}`}
        {...props}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  // Custom dropdown when menuProps is provided
  const selectedOption = options.find(o => String(o.value) === String(value));

  const handleOptionClick = (optValue) => {
    // Simulate native event shape used by forms in this codebase
    if (onChange) onChange({ target: { name, value: optValue } });
    setIsOpen(false);
  };

  const mergedMenuStyle = Object.assign({
    maxHeight: '200px',
    minHeight: '80px',
    overflowY: 'auto',
    zIndex: 50,
    scrollBehavior: 'smooth',
  }, menuProps.style || {});

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`${CONTROL_BASE} py-1.5 text-left min-h-[40px] sm:min-h-[34px] ${controlState(error, disabled)} flex justify-between items-center`}
        onClick={() => !disabled && setIsOpen(o => !o)}
        {...props}
      >
        <span className={`truncate ${selectedOption ? '' : 'text-gray-400'}`}>{selectedOption ? selectedOption.label : placeholder}</span>
        <svg className="ml-2 h-4 w-4 flex-shrink-0 text-gray-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
        </svg>
      </button>

      {isOpen && (
        <div role="listbox" className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg" style={mergedMenuStyle}>
          {options.length === 0 ? (
            <div className="p-2 text-gray-500">No options</div>
          ) : (
            options.map(opt => (
              <div
                key={opt.value}
                role="option"
                aria-selected={String(opt.value) === String(value)}
                className={`px-3 py-2 sm:py-1.5 hover:bg-gray-100 cursor-pointer ${String(opt.value) === String(value) ? 'bg-blue-50 font-medium text-blue-700' : ''}`}
                onClick={() => handleOptionClick(opt.value)}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export const TextArea = ({
  placeholder,
  value,
  onChange,
  error,
  rows = 4,
  disabled = false,
  className = '',
  ...props
}) => {
  return (
    <textarea
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      rows={rows}
      disabled={disabled}
      aria-invalid={error ? true : undefined}
      className={`${CONTROL_BASE} py-1.5 resize-y ${controlState(error, disabled)} ${className}`}
      {...props}
    />
  );
};

const BUTTON_VARIANTS = {
  primary: 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow-sm focus-visible:ring-blue-500',
  secondary: 'bg-amber-400 hover:bg-amber-500 text-gray-900 shadow-sm focus-visible:ring-amber-500',
  success: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm focus-visible:ring-emerald-500',
  danger: 'bg-red-600 hover:bg-red-700 text-white shadow-sm focus-visible:ring-red-500',
  warning: 'bg-amber-400 hover:bg-amber-500 text-gray-900 shadow-sm focus-visible:ring-amber-500',
  outline: 'border border-gray-300 hover:bg-gray-50 text-gray-700 bg-white shadow-sm focus-visible:ring-blue-500',
  ghost: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-blue-500',
  'danger-outline': 'border border-red-200 text-red-600 bg-white hover:bg-red-50 focus-visible:ring-red-500',
};

const BUTTON_SIZES = {
  sm: 'px-2.5 py-1 text-xs sm:text-sm min-h-[28px] gap-1.5',
  md: 'px-3 py-1.5 text-sm min-h-[38px] sm:min-h-[32px] gap-1.5',
  lg: 'px-4 py-2 text-sm sm:text-base min-h-[40px] gap-2',
};

export const Button = ({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  className = '',
  icon: Icon,
  ...props
}) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`inline-flex items-center justify-center font-medium rounded-lg transition-colors touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary} ${BUTTON_SIZES[size] || BUTTON_SIZES.md} ${
        disabled || loading ? 'opacity-50 cursor-not-allowed' : ''
      } ${className}`}
      {...props}
    >
      {loading ? (
        <>
          <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>{typeof children === 'string' ? 'Please wait…' : children}</span>
        </>
      ) : (
        <>
          {Icon && <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />}
          {children}
        </>
      )}
    </button>
  );
};
